// frontend/src/pages/Dashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getDashboardStats,
  getRecentOrders,
  getLowStockProducts,
  getAllOrders,
  getAllShipments,
  getProducts,
  deleteProduct,
  updateOrderStatus,
  updateShipmentStatus,
  getUsers,
  getAnalytics,
  createTeamMember,
  toggleUserStatus,
  getAdminUserStats,
  getUserOrders,
  getMyUserStats,
  updateProfile,
  changePassword,
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  getPayoutHistory,
  requestPayout,
  getAllPreOrders,
  getPreOrderStats,
  updatePreOrderStatus,
  getAvailableSizes,
  getPreOrderSizes,
} from "../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faShoppingCart,
  faUsers,
  faTruck,
  faEdit,
  faTrash,
  faChartLine,
  faUserPlus,
  faToggleOn,
  faToggleOff,
  faTractor,
  faCreditCard,
  faHistory,
  faDollarSign,
  faUserCircle,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faSave,
  faKey,
  faPlus,
  faWallet,
  faMobileAlt,
  faQuoteLeft,
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faSearch,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
  faClock,
  faCrown,
  faShieldAlt,
  faStore,
  faSpinner,
  faClipboardList,
  faCalendarCheck,
  faMoneyBillWave,
  faGlobe,
  faWeightScale,
  faBoxes,
  faShip,
  faWhatsapp,
  faSeedling,
  faArrowRight,
  faBuilding,
  faAnchor,
  faRocket,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import Loader from "../components/UI/Loader";
import ProductForm from "../components/Dashboard/ProductForm";
import ShipmentForm from "../components/Dashboard/ShipmentForm";
import AnalyticsCards from "../components/Dashboard/AnalyticsCards";
import SalesChart from "../components/Dashboard/SalesChart";
import PaymentMethods from "../components/Dashboard/PaymentMethods";
import PayoutComponent from "../components/Dashboard/PayoutComponent";
import TrafficSources from "../components/Dashboard/TrafficSources";
import TopProducts from "../components/Dashboard/TopProducts";
import TeamMemberForm from "../components/Dashboard/TeamMemberForm";
import TestimonialManager from "../components/Dashboard/TestimonialManager";
import OrderDetailsModal from "../components/Dashboard/OrderDetailsModal";
import ShipmentDetailsModal from "../components/Dashboard/ShipmentDetailsModal";
import PreOrderManager from "../components/Dashboard/PreOrderManager";
import "../styles/dashboard.scss";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getProductImage = (product) => {
  if (product?.images?.[0]) return product.images[0];
  if (product?.thumbnail) return product.thumbnail;
  if (product?.image) return product.image;
  return "/placeholder.png";
};

const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
    ordered: "#D9A321",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    delayed: "#dc2626",
    completed: "#16a34a",
    failed: "#dc2626",
    paid: "#16a34a",
    unpaid: "#dc2626",
    processing_payment: "#f59e0b",
    failed_payment: "#dc2626",
    refunded_payment: "#6b7280",
    deposit_received: "#8b5cf6",
    ready_for_pickup: "#10b981",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "⏳ Pending",
    processing: "⚙️ Processing",
    confirmed: "✅ Confirmed",
    shipped: "🚢 Shipped",
    out_for_delivery: "📬 Out for Delivery",
    delivered: "📦 Delivered",
    cancelled: "❌ Cancelled",
    refunded: "↩️ Refunded",
    ordered: "📋 Order Received",
    in_transit: "🚚 In Transit",
    customs: "🛃 Customs Clearance",
    delayed: "⚠️ Delayed",
    completed: "✅ Completed",
    failed: "❌ Failed",
    paid: "💰 Paid",
    unpaid: "💳 Unpaid",
    processing_payment: "💳 Processing Payment",
    failed_payment: "❌ Payment Failed",
    refunded_payment: "↩️ Refunded",
    deposit_received: "💵 Deposit Received",
    ready_for_pickup: "📦 Ready for Pickup",
  };
  return labels[status] || status;
};

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
    case "completed":
      return faCheckCircle;
    case "cancelled":
    case "failed":
      return faTimes;
    case "pending":
    case "processing":
    case "confirmed":
      return faClock;
    case "shipped":
    case "out_for_delivery":
    case "in_transit":
      return faTruck;
    case "deposit_received":
      return faMoneyBillWave;
    case "ready_for_pickup":
      return faCalendarCheck;
    default:
      return faExclamationTriangle;
  }
};

const safeArray = (data) => (Array.isArray(data) ? data : []);

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================
const Dashboard = () => {
  const { user, refreshUser, logout, isAdmin, isSuperAdmin } = useAuth();

  // State
  const [stats, setStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [allShipments, setAllShipments] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [preOrders, setPreOrders] = useState([]);
  const [preOrderStats, setPreOrderStats] = useState({});
  const [availableSizes, setAvailableSizes] = useState([]);
  const [preOrderSizes, setPreOrderSizes] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalVisitors: 0,
    conversionRate: 0,
    avgOrderValue: 0,
    cartAbandonment: 0,
    clv: 0,
    repeatRate: 0,
    salesData: [],
    topProducts: [],
    trafficSources: [],
    orderStatus: {
      pending: 0,
      processing: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0,
    },
    recentActivity: [],
    preOrders: {
      total: 0,
      revenue: 0,
      deposits: 0,
      totalPacks: 0,
      totalKg: 0,
    },
  });
  const [myOrders, setMyOrders] = useState([]);
  const [myStats, setMyStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [shipmentUpdateData, setShipmentUpdateData] = useState({
    status: "",
    location: "",
    description: "",
  });
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: { street: "", city: "", district: "", country: "Sierra Leone" },
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [profileMessage, setProfileMessage] = useState({ type: "", text: "" });
  const [updating, setUpdating] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: "card",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    nameOnCard: "",
    isDefault: false,
  });
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutRequest, setPayoutRequest] = useState({
    amount: "",
    method: "bank_transfer",
    accountDetails: "",
  });
  const [originStats, setOriginStats] = useState({});
  const [shipmentStats, setShipmentStats] = useState({});

  const userRole = user?.role || "customer";
  const userType = user?.userType || "customer";
  const isAdminUser = isAdmin?.() || false;
  const isSuperAdminUser = isSuperAdmin?.() || false;
  const isFarmer = userType === "farmer" || userRole === "farmer";

  // ============================================
  // DATA FETCHING
  // ============================================
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const userOrdersPromise = getUserOrders();
      let promises = [userOrdersPromise];

      if (isAdminUser || isSuperAdminUser) {
        promises.push(
          getDashboardStats(),
          getRecentOrders(),
          getLowStockProducts(),
          getAllOrders({ limit: 50 }),
          getAllShipments({ limit: 50 }),
          getProducts({ limit: 100 }),
          getUsers(),
          getAnalytics("30d"),
          getAdminUserStats(),
          getAllPreOrders({ limit: 50 }),
          getPreOrderStats(),
          getAvailableSizes(),
          getPreOrderSizes(),
        );
      } else {
        promises.push(getMyUserStats());
      }

      const results = await Promise.all(promises);
      setMyOrders(safeArray(results[0]?.data));

      if (isAdminUser || isSuperAdminUser) {
        setStats(results[1]?.data || {});
        setRecentOrders(safeArray(results[2]?.data));
        setLowStockProducts(safeArray(results[3]?.data));
        setAllOrders(safeArray(results[4]?.data?.orders || results[4]?.data));
        setAllShipments(
          safeArray(results[5]?.data?.shipments || results[5]?.data),
        );
        setAllProducts(
          safeArray(results[6]?.data?.products || results[6]?.data),
        );
        setUsers(safeArray(results[7]?.data?.users || results[7]?.data));

        const analyticsData = results[8]?.data || {};
        setAnalytics({
          ...analyticsData,
          orderStatus: {
            pending: analyticsData.pendingOrders || 0,
            processing: analyticsData.processingOrders || 0,
            shipped: analyticsData.shippedOrders || 0,
            delivered: analyticsData.deliveredOrders || 0,
            cancelled: analyticsData.cancelledOrders || 0,
          },
          recentActivity: safeArray(analyticsData.recentActivity),
          preOrders: analyticsData.preOrders || {
            total: 0,
            revenue: 0,
            deposits: 0,
            totalPacks: 0,
            totalKg: 0,
          },
        });
        setUserStats(results[9]?.data || {});
        setPreOrders(safeArray(results[10]?.data?.data || results[10]?.data));
        setPreOrderStats(results[11]?.data?.data || results[11]?.data || {});
        setAvailableSizes(results[12]?.data?.sizes || []);
        setPreOrderSizes(results[13]?.data?.sizes || []);

        // Extract origin stats from products
        if (results[6]?.data?.products) {
          const origins = {};
          results[6].data.products.forEach((p) => {
            const origin = p.origin || SUPPLIER.country;
            origins[origin] = (origins[origin] || 0) + 1;
          });
          setOriginStats(origins);
        }

        // Extract shipment stats
        if (results[5]?.data) {
          const shipments = results[5].data.shipments || results[5].data || [];
          const stats = {
            total: shipments.length,
            fromMalaysia: shipments.filter(
              (s) => s.origin?.country === SUPPLIER.country,
            ).length,
            bulk: shipments.filter((s) => s.orderType === "bulk_farmer").length,
            inventory: shipments.filter((s) => s.orderType === "inventory")
              .length,
          };
          setShipmentStats(stats);
        }
      } else {
        setMyStats(results[1]?.data || {});
        if (user) {
          setProfileData({
            name: user.name || "",
            email: user.email || "",
            phone: user.phone || "",
            address: user.address || {
              street: "",
              city: "",
              district: "",
              country: "Sierra Leone",
            },
          });
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setUsers([]);
      setNotification({
        type: "error",
        message: "Failed to load dashboard data",
      });
    } finally {
      setLoading(false);
    }
  }, [isAdminUser, isSuperAdminUser, user]);

  const fetchPaymentData = useCallback(async () => {
    try {
      const [methods, history] = await Promise.all([
        getPaymentMethods(),
        getPayoutHistory(),
      ]);
      setPaymentMethods(safeArray(methods?.data));
      setPayoutHistory(safeArray(history?.data));
    } catch (error) {
      console.error("Error fetching payment data:", error);
      setPaymentMethods([]);
      setPayoutHistory([]);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    fetchPaymentData();
  }, [fetchAllData, fetchPaymentData]);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: "", message: "" }), 5000);
  };

  // ============================================
  // PROFILE HANDLERS
  // ============================================
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setProfileMessage({ type: "", text: "" });
    try {
      const response = await updateProfile(profileData);
      if (response.data.success) {
        setProfileMessage({
          type: "success",
          text: "Profile updated successfully!",
        });
        await refreshUser();
        showNotification("success", "Profile updated successfully!");
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to update profile";
      setProfileMessage({ type: "error", text: msg });
      showNotification("error", msg);
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setProfileMessage({ type: "error", text: "New passwords do not match" });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setProfileMessage({
        type: "error",
        text: "Password must be at least 6 characters",
      });
      return;
    }
    setUpdating(true);
    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      setProfileMessage({
        type: "success",
        text: "Password changed successfully!",
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      showNotification("success", "Password changed successfully!");
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to change password";
      setProfileMessage({ type: "error", text: msg });
      showNotification("error", msg);
    } finally {
      setUpdating(false);
    }
  };

  // ============================================
  // PAYMENT HANDLERS
  // ============================================
  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    try {
      await addPaymentMethod(newPaymentMethod);
      setShowAddPaymentModal(false);
      fetchPaymentData();
      setNewPaymentMethod({
        type: "card",
        cardNumber: "",
        expiryDate: "",
        cvv: "",
        nameOnCard: "",
        isDefault: false,
      });
      showNotification("success", "Payment method added successfully!");
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to add payment method";
      showNotification("error", msg);
    }
  };

  const handleRemovePaymentMethod = async (methodId) => {
    if (
      window.confirm("Are you sure you want to remove this payment method?")
    ) {
      try {
        await removePaymentMethod(methodId);
        fetchPaymentData();
        showNotification("success", "Payment method removed successfully!");
      } catch (error) {
        const msg =
          error.response?.data?.message || "Failed to remove payment method";
        showNotification("error", msg);
      }
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    try {
      await requestPayout(payoutRequest);
      setShowPayoutModal(false);
      fetchPaymentData();
      setPayoutRequest({
        amount: "",
        method: "bank_transfer",
        accountDetails: "",
      });
      showNotification("success", "Payout requested successfully!");
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to request payout";
      showNotification("error", msg);
    }
  };

  // ============================================
  // ADMIN CRUD HANDLERS
  // ============================================
  const handleUpdateOrderStatus = async (orderId, status) => {
    if (!isAdminUser && !isSuperAdminUser) return;
    try {
      await updateOrderStatus(orderId, status);
      await fetchAllData();
      showNotification(
        "success",
        `Order status updated to ${getStatusLabel(status)}`,
      );
    } catch (error) {
      console.error("Error updating order status:", error);
      showNotification("error", "Failed to update order status");
    }
  };

  const handleUpdateShipmentStatus = async (shipmentId) => {
    if (!isAdminUser && !isSuperAdminUser) return;
    try {
      await updateShipmentStatus(
        shipmentId,
        shipmentUpdateData.status,
        shipmentUpdateData.location,
        shipmentUpdateData.description,
      );
      setSelectedShipment(null);
      setShipmentUpdateData({ status: "", location: "", description: "" });
      await fetchAllData();
      showNotification("success", "Shipment status updated successfully!");
    } catch (error) {
      console.error("Error updating shipment status:", error);
      showNotification("error", "Failed to update shipment status");
    }
  };

  const handleUpdatePreOrderStatus = async (preOrderId, data) => {
    if (!isAdminUser && !isSuperAdminUser) return;
    try {
      await updatePreOrderStatus(preOrderId, data);
      await fetchAllData();
      showNotification(
        "success",
        `Pre-order status updated to ${getStatusLabel(data.status)}`,
      );
    } catch (error) {
      console.error("Error updating pre-order status:", error);
      showNotification("error", "Failed to update pre-order status");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!isAdminUser && !isSuperAdminUser) return;
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        await fetchAllData();
        showNotification("success", "Product deleted successfully!");
      } catch (error) {
        console.error("Error deleting product:", error);
        showNotification("error", "Failed to delete product");
      }
    }
  };

  const handleToggleUserStatus = async (userId) => {
    if (!isAdminUser && !isSuperAdminUser) return;
    if (userId === user?._id) {
      showNotification("error", "You cannot change your own account status");
      return;
    }
    try {
      await toggleUserStatus(userId);
      await fetchAllData();
      showNotification("success", "User status updated successfully!");
    } catch (error) {
      console.error("Error toggling user status:", error);
      const msg =
        error.response?.data?.message || "Failed to update user status";
      showNotification("error", msg);
    }
  };

  // ============================================
  // SORTING HELPERS
  // ============================================
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  // Dashboard title and tabs logic
  const getDashboardTitle = () => {
    if (isSuperAdminUser) return "👑 Super Admin Dashboard";
    if (isAdminUser) return "🛡️ Admin Dashboard";
    if (isFarmer) return "🌾 Farmer Dashboard";
    return "🛒 My Account";
  };

  const getDashboardTabs = () => {
    const tabs = [
      { id: "overview", label: "Overview", icon: faChartLine },
      { id: "orders", label: "My Orders", icon: faShoppingCart },
      { id: "profile", label: "Profile", icon: faUserCircle },
      { id: "payments", label: "Payments", icon: faCreditCard },
      { id: "testimonials", label: "Testimonials", icon: faQuoteLeft },
    ];
    if (isAdminUser || isSuperAdminUser) {
      tabs.splice(
        1,
        0,
        { id: "orders-all", label: "All Orders", icon: faShoppingCart },
        { id: "preorders", label: "Pre-Orders", icon: faClipboardList },
        { id: "shipments", label: "Shipments", icon: faTruck },
        { id: "products", label: "Products", icon: faBox },
        { id: "users", label: "Users", icon: faUsers },
        { id: "analytics", label: "Analytics", icon: faChartLine },
      );
    }
    return tabs;
  };

  return (
    <div className="dashboard-container">
      {/* Notification */}
      {notification.message && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon
            icon={
              notification.type === "success"
                ? faCheckCircle
                : faExclamationTriangle
            }
          />
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ type: "", message: "" })}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>
            {isSuperAdminUser && (
              <FontAwesomeIcon icon={faCrown} className="role-icon" />
            )}
            {isAdminUser && !isSuperAdminUser && (
              <FontAwesomeIcon icon={faShieldAlt} className="role-icon" />
            )}
            {isFarmer && !isAdminUser && (
              <FontAwesomeIcon icon={faTractor} className="role-icon" />
            )}
            {!isFarmer && !isAdminUser && !isSuperAdminUser && (
              <FontAwesomeIcon icon={faStore} className="role-icon" />
            )}
            {getDashboardTitle()}
          </h1>
          <p>Welcome back, {user?.name}</p>
          {isSuperAdminUser && (
            <span className="role-badge super-admin">Super Admin</span>
          )}
          {isAdminUser && !isSuperAdminUser && (
            <span className="role-badge admin">Admin</span>
          )}
          {isFarmer && !isAdminUser && (
            <span className="role-badge farmer">Farmer</span>
          )}
        </div>
        <div className="header-actions">
          <a
            href="https://wa.me/23231030808"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-support"
          >
            <FontAwesomeIcon icon={faWhatsappBrand} /> Support
          </a>
          <button onClick={logout} className="logout-btn">
            <FontAwesomeIcon icon={faTimes} /> Logout
          </button>
        </div>
      </div>

      {/* ========== STATS GRID ========== */}
      <div className="stats-grid">
        {isAdminUser || isSuperAdminUser ? (
          <>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBox} />
              </div>
              <div className="stat-info">
                <h3>Total Products</h3>
                <div className="stat-value">{stats.totalProducts || 0}</div>
                <div className="stat-sub">
                  <FontAwesomeIcon icon={faGlobe} />{" "}
                  {Object.entries(originStats)
                    .map(([origin, count]) => `${origin}: ${count}`)
                    .join(" | ")}
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faShoppingCart} />
              </div>
              <div className="stat-info">
                <h3>Total Orders</h3>
                <div className="stat-value">{stats.totalOrders || 0}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="stat-info">
                <h3>Total Users</h3>
                <div className="stat-value">{stats.totalUsers || 0}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faTruck} />
              </div>
              <div className="stat-info">
                <h3>Active Shipments</h3>
                <div className="stat-value">{stats.activeShipments || 0}</div>
                {shipmentStats.fromMalaysia > 0 && (
                  <div className="stat-sub">
                    <FontAwesomeIcon icon={faShip} /> From {SUPPLIER.country}:{" "}
                    {shipmentStats.fromMalaysia}
                  </div>
                )}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="stat-info">
                <h3>Total Revenue</h3>
                <div className="stat-value">
                  ${(stats.totalRevenue || 0).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="stat-card" style={{ borderColor: "#8b5cf6" }}>
              <div
                className="stat-icon"
                style={{ background: "rgba(139, 92, 246, 0.15)" }}
              >
                <FontAwesomeIcon
                  icon={faClipboardList}
                  style={{ color: "#8b5cf6" }}
                />
              </div>
              <div className="stat-info">
                <h3>Pre-Orders</h3>
                <div className="stat-value">
                  {preOrderStats.totalPreOrders || 0}
                </div>
                <div className="stat-sub">
                  {availableSizes.length > 0 && (
                    <span>
                      <FontAwesomeIcon icon={faWeightScale} /> Sizes:{" "}
                      {availableSizes.join("kg, ")}kg
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faShoppingCart} />
              </div>
              <div className="stat-info">
                <h3>My Orders</h3>
                <div className="stat-value">{myStats.totalOrders || 0}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBox} />
              </div>
              <div className="stat-info">
                <h3>Total Items</h3>
                <div className="stat-value">{myStats.totalItems || 0}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="stat-info">
                <h3>Total Spent</h3>
                <div className="stat-value">
                  ${(myStats.totalSpent || 0).toLocaleString()}
                </div>
              </div>
            </div>
            {isFarmer && (
              <div className="stat-card" style={{ borderColor: "#D9A321" }}>
                <div
                  className="stat-icon"
                  style={{ background: "rgba(217, 163, 33, 0.15)" }}
                >
                  <FontAwesomeIcon
                    icon={faTractor}
                    style={{ color: "#D9A321" }}
                  />
                </div>
                <div className="stat-info">
                  <h3>Bulk Orders</h3>
                  <div className="stat-value">{myStats.bulkOrders || 0}</div>
                </div>
              </div>
            )}
            {myStats.preOrders?.total > 0 && (
              <div className="stat-card" style={{ borderColor: "#8b5cf6" }}>
                <div
                  className="stat-icon"
                  style={{ background: "rgba(139, 92, 246, 0.15)" }}
                >
                  <FontAwesomeIcon
                    icon={faClipboardList}
                    style={{ color: "#8b5cf6" }}
                  />
                </div>
                <div className="stat-info">
                  <h3>My Pre-Orders</h3>
                  <div className="stat-value">
                    {myStats.preOrders?.total || 0}
                  </div>
                  <div className="stat-sub">
                    {myStats.preOrders?.totalKg || 0} kg total
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== LOW STOCK ALERT ========== */}
      {lowStockProducts.length > 0 && (isAdminUser || isSuperAdminUser) && (
        <div className="alert-section">
          <h3>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Low Stock Alert
          </h3>
          <div className="low-stock-grid">
            {lowStockProducts.map((product) => (
              <div key={product._id} className="low-stock-card">
                <span>
                  {product.name}
                  {product.origin && (
                    <span className="origin-tag">
                      <FontAwesomeIcon icon={faGlobe} /> {product.origin}
                    </span>
                  )}
                </span>
                <span className="stock-count">{product.stock} units left</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== SUPPLIER INFO ========== */}
      <div className="supplier-info-section">
        <div className="supplier-info-card">
          <div className="supplier-info-icon">
            <FontAwesomeIcon icon={faShip} />
          </div>
          <div className="supplier-info-content">
            <h4>Sourced from {SUPPLIER.name}</h4>
            <p>
              <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} &bull;
              <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
            </p>
            <span className="supplier-badge">
              <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
            </span>
          </div>
        </div>
      </div>

      {/* ========== TABS ========== */}
      <div className="dashboard-tabs">
        {getDashboardTabs().map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>

      {/* ========== TAB CONTENT ========== */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="recent-section">
            <h3>
              {isAdminUser || isSuperAdminUser
                ? "Recent Orders"
                : "My Recent Orders"}
            </h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(isAdminUser || isSuperAdminUser
                    ? recentOrders
                    : myOrders.slice(0, 5)
                  ).map((order) => (
                    <tr key={order._id}>
                      <td>{order.orderNumber}</td>
                      <td>{order.customerName || order.userName}</td>
                      <td>
                        $
                        {(
                          order.total?.usd ||
                          order.totalAmount?.usd ||
                          0
                        ).toLocaleString()}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ background: getStatusColor(order.status) }}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => setSelectedOrder(order)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* My Orders Tab */}
        {activeTab === "orders" && (
          <div className="recent-section">
            <h3>My Orders</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Date</th>
                    <th>Items</th>
                    <th>Quantity</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {myOrders.map((order) => (
                    <tr
                      key={order._id}
                      className={
                        order.orderType === "bulk_farmer"
                          ? "bulk-order-row"
                          : ""
                      }
                    >
                      <td>
                        <strong>{order.orderNumber}</strong>
                        {order.orderType === "bulk_farmer" && (
                          <span className="bulk-badge">BULK</span>
                        )}
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td>{order.items?.length || 0} items</td>
                      <td>{order.totalQuantity || 0} kg</td>
                      <td>
                        $
                        {(
                          order.total?.usd ||
                          order.totalAmount?.usd ||
                          0
                        ).toLocaleString()}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ background: getStatusColor(order.status) }}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => setSelectedOrder(order)}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Orders Tab (Admin) */}
        {activeTab === "orders-all" && (isAdminUser || isSuperAdminUser) && (
          <div className="tab-content">
            <div className="section-header">
              <h2>All Orders</h2>
              <div className="filters">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="shipped">Shipped</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Items</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allOrders
                    .filter((o) => !filterStatus || o.status === filterStatus)
                    .filter(
                      (o) =>
                        !searchTerm ||
                        o.orderNumber
                          ?.toLowerCase()
                          .includes(searchTerm.toLowerCase()),
                    )
                    .map((order) => (
                      <tr
                        key={order._id}
                        className={
                          order.orderType === "bulk_farmer"
                            ? "bulk-order-row"
                            : ""
                        }
                      >
                        <td>
                          <strong>{order.orderNumber}</strong>
                          {order.orderType === "bulk_farmer" && (
                            <span className="bulk-badge">BULK</span>
                          )}
                        </td>
                        <td>{order.customerName || order.userName}</td>
                        <td>{order.customerEmail || order.userEmail}</td>
                        <td>{order.items?.length || 0} items</td>
                        <td>
                          $
                          {(
                            order.total?.usd ||
                            order.totalAmount?.usd ||
                            0
                          ).toLocaleString()}
                        </td>
                        <td>
                          <span
                            className={`order-type-badge ${order.orderType || "retail"}`}
                          >
                            {order.orderType === "bulk_farmer"
                              ? "🌾 Bulk"
                              : "🛒 Retail"}
                          </span>
                        </td>
                        <td>
                          <select
                            value={order.status}
                            onChange={(e) =>
                              handleUpdateOrderStatus(order._id, e.target.value)
                            }
                            className="status-select"
                            style={{
                              borderColor: getStatusColor(order.status),
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="shipped">Shipped</option>
                            <option value="out_for_delivery">
                              Out for Delivery
                            </option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className="view-btn"
                            onClick={() => setSelectedOrder(order)}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pre-Orders Tab */}
        {activeTab === "preorders" && (isAdminUser || isSuperAdminUser) && (
          <PreOrderManager
            preOrders={preOrders}
            stats={preOrderStats}
            onUpdateStatus={handleUpdatePreOrderStatus}
            onRefresh={fetchAllData}
            availableSizes={availableSizes}
            preOrderSizes={preOrderSizes}
            supplier={SUPPLIER}
          />
        )}

        {/* Shipments Tab */}
        {activeTab === "shipments" && (isAdminUser || isSuperAdminUser) && (
          <div className="tab-content">
            <div className="section-header">
              <h2>Shipment Tracking</h2>
              <div className="filters">
                <span className="shipment-stats">
                  <FontAwesomeIcon icon={faShip} /> From {SUPPLIER.country}:{" "}
                  {shipmentStats.fromMalaysia || 0}
                  <span className="stat-sep">|</span>
                  <FontAwesomeIcon icon={faBoxes} /> Bulk:{" "}
                  {shipmentStats.bulk || 0}
                </span>
                <button
                  className="btn-primary"
                  onClick={() => setShowShipmentForm(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create Shipment
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tracking #</th>
                    <th>Order #</th>
                    <th>Type</th>
                    <th>Origin</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Est. Delivery</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allShipments.map((shipment) => (
                    <tr
                      key={shipment._id}
                      className={
                        shipment.orderType === "bulk_farmer"
                          ? "bulk-shipment-row"
                          : ""
                      }
                    >
                      <td>
                        <strong>{shipment.trackingNumber}</strong>
                        {shipment.origin?.country === SUPPLIER.country && (
                          <span className="origin-badge">
                            <FontAwesomeIcon icon={faShip} /> MY
                          </span>
                        )}
                      </td>
                      <td>{shipment.order?.orderNumber || "Inventory"}</td>
                      <td>
                        <span
                          className={`shipment-type-badge ${shipment.orderType || "retail"}`}
                        >
                          {shipment.orderType === "bulk_farmer"
                            ? "🚢 Bulk"
                            : shipment.orderType === "inventory"
                              ? "📦 Inventory"
                              : "📦 Standard"}
                        </span>
                      </td>
                      <td>
                        {shipment.origin?.city || SUPPLIER.city}
                        {shipment.origin?.country && (
                          <span className="country-tag">
                            {shipment.origin.country}
                          </span>
                        )}
                      </td>
                      <td>
                        {shipment.destination?.city || "Freetown, Sierra Leone"}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            background: getStatusColor(shipment.status),
                          }}
                        >
                          {getStatusLabel(shipment.status)}
                        </span>
                      </td>
                      <td>
                        {shipment.estimatedDelivery
                          ? new Date(
                              shipment.estimatedDelivery,
                            ).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => setSelectedShipment(shipment)}
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === "products" && (isAdminUser || isSuperAdminUser) && (
          <div className="tab-content">
            <div className="section-header">
              <h2>Product Management</h2>
              <div className="filters">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="size-indicator">
                  <FontAwesomeIcon icon={faWeightScale} /> Sizes:{" "}
                  {availableSizes.length > 0
                    ? availableSizes.join("kg, ") + "kg"
                    : "2kg, 10kg, 25kg, 50kg"}
                  {preOrderSizes.length > 0 && (
                    <span className="preorder-sizes">
                      {" "}
                      | Pre-order: {preOrderSizes.join("kg, ")}kg
                    </span>
                  )}
                </span>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductForm(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Product
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th onClick={() => handleSort("name")}>
                      Name <FontAwesomeIcon icon={getSortIcon("name")} />
                    </th>
                    <th onClick={() => handleSort("category")}>
                      Category{" "}
                      <FontAwesomeIcon icon={getSortIcon("category")} />
                    </th>
                    <th onClick={() => handleSort("origin")}>
                      Origin <FontAwesomeIcon icon={getSortIcon("origin")} />
                    </th>
                    <th onClick={() => handleSort("basePrice.usd")}>
                      Price{" "}
                      <FontAwesomeIcon icon={getSortIcon("basePrice.usd")} />
                    </th>
                    <th onClick={() => handleSort("stock")}>
                      Stock <FontAwesomeIcon icon={getSortIcon("stock")} />
                    </th>
                    <th>Sizes</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allProducts
                    .filter(
                      (p) =>
                        !searchTerm ||
                        p.name
                          ?.toLowerCase()
                          .includes(searchTerm.toLowerCase()),
                    )
                    .sort((a, b) => {
                      let aVal = a[sortField] || "",
                        bVal = b[sortField] || "";
                      if (sortField === "basePrice.usd") {
                        aVal = a.basePrice?.usd || 0;
                        bVal = b.basePrice?.usd || 0;
                      }
                      if (typeof aVal === "string")
                        return sortDirection === "asc"
                          ? aVal.localeCompare(bVal)
                          : bVal.localeCompare(aVal);
                      return sortDirection === "asc"
                        ? aVal - bVal
                        : bVal - aVal;
                    })
                    .map((product) => (
                      <tr key={product._id}>
                        <td className="product-image-cell">
                          <img
                            src={getProductImage(product)}
                            alt={product.name}
                            className="product-thumb"
                          />
                        </td>
                        <td>
                          <strong>{product.name}</strong>
                        </td>
                        <td>{product.category?.replace(/-/g, " ")}</td>
                        <td>
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {product.origin || SUPPLIER.country}
                          </span>
                        </td>
                        <td>
                          ${product.basePrice?.usd || product.pricing?.usd || 0}
                          /{product.unit || "kg"}
                        </td>
                        <td
                          className={
                            product.stock < (product.lowStockThreshold || 100)
                              ? "low-stock"
                              : ""
                          }
                        >
                          {product.stock}
                        </td>
                        <td>
                          <span className="size-tags">
                            {product.availableSizes?.length > 0 ? (
                              product.availableSizes.map((size) => (
                                <span key={size} className="size-tag">
                                  {size}kg
                                  {product.preOrderSizes?.includes(size) && (
                                    <span className="preorder-tag">📋</span>
                                  )}
                                </span>
                              ))
                            ) : (
                              <span className="size-tag">2, 10, 25, 50kg</span>
                            )}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${product.isAvailable ? "active" : "inactive"}`}
                          >
                            {product.isAvailable ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="action-buttons">
                          <button
                            className="edit-btn"
                            onClick={() => {
                              setEditingProduct(product);
                              setShowProductForm(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteProduct(product._id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (isAdminUser || isSuperAdminUser) && (
          <div className="tab-content">
            <div className="section-header">
              <h2>User Management</h2>
              <div className="header-actions">
                <button
                  className="btn-primary"
                  onClick={() => {
                    setEditingTeamMember(null);
                    setShowTeamMemberForm(true);
                  }}
                >
                  <FontAwesomeIcon icon={faUserPlus} /> Add Team Member
                </button>
              </div>
            </div>
            <div className="user-stats-grid">
              <div className="user-stat-card">
                <div className="user-stat-icon">👥</div>
                <div className="user-stat-info">
                  <div className="user-stat-value">
                    {userStats.totalUsers || 0}
                  </div>
                  <div className="user-stat-label">Total Users</div>
                </div>
              </div>
              <div className="user-stat-card">
                <div className="user-stat-icon">👑</div>
                <div className="user-stat-info">
                  <div className="user-stat-value">
                    {userStats.adminUsers || 0}
                  </div>
                  <div className="user-stat-label">Admins</div>
                </div>
              </div>
              {isSuperAdminUser && (
                <div
                  className="user-stat-card"
                  style={{ borderColor: "#D9A321" }}
                >
                  <div className="user-stat-icon">⭐</div>
                  <div className="user-stat-info">
                    <div
                      className="user-stat-value"
                      style={{ color: "#D9A321" }}
                    >
                      {userStats.superAdmin || 1}
                    </div>
                    <div className="user-stat-label">Super Admins</div>
                  </div>
                </div>
              )}
              <div className="user-stat-card">
                <div className="user-stat-icon">🛒</div>
                <div className="user-stat-info">
                  <div className="user-stat-value">
                    {userStats.customerUsers || 0}
                  </div>
                  <div className="user-stat-label">Customers</div>
                </div>
              </div>
              <div className="user-stat-card">
                <div className="user-stat-icon">🌾</div>
                <div className="user-stat-info">
                  <div className="user-stat-value">
                    {userStats.farmerUsers || 0}
                  </div>
                  <div className="user-stat-label">Farmers</div>
                </div>
              </div>
              <div className="user-stat-card">
                <div className="user-stat-icon">📅</div>
                <div className="user-stat-info">
                  <div className="user-stat-value">
                    {userStats.newUsersThisMonth || 0}
                  </div>
                  <div className="user-stat-label">New This Month</div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Type</th>
                    <th>Phone</th>
                    <th>Joined</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(users) && users.length > 0 ? (
                    users.map((userItem) => (
                      <tr
                        key={userItem._id}
                        className={!userItem.isActive ? "inactive-row" : ""}
                      >
                        <td>
                          <strong>{userItem.name}</strong>
                          {userItem._id === user?._id && (
                            <span className="current-user-badge">You</span>
                          )}
                        </td>
                        <td>{userItem.email}</td>
                        <td>
                          <span className={`role-badge ${userItem.role}`}>
                            {userItem.role === "super_admin" && (
                              <FontAwesomeIcon
                                icon={faCrown}
                                style={{ marginRight: "0.25rem" }}
                              />
                            )}
                            {userItem.role === "admin" && (
                              <FontAwesomeIcon
                                icon={faShieldAlt}
                                style={{ marginRight: "0.25rem" }}
                              />
                            )}
                            {userItem.role === "super_admin"
                              ? "Super Admin"
                              : userItem.role === "admin"
                                ? "Admin"
                                : userItem.role === "farmer"
                                  ? "Farmer"
                                  : "Customer"}
                          </span>
                        </td>
                        <td>
                          {userItem.userType === "farmer" ? (
                            <span className="farmer-badge">
                              {userItem.farmDetails?.farmSize || "small"} farm
                            </span>
                          ) : userItem.userType === "supplier" ? (
                            <span className="supplier-badge">Supplier</span>
                          ) : (
                            <span className="customer-badge">Retail</span>
                          )}
                        </td>
                        <td>{userItem.phone || "-"}</td>
                        <td>
                          {new Date(userItem.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className={`status-toggle ${userItem.isActive ? "active" : "inactive"}`}
                            onClick={() => handleToggleUserStatus(userItem._id)}
                            disabled={userItem._id === user?._id}
                          >
                            <FontAwesomeIcon
                              icon={
                                userItem.isActive ? faToggleOn : faToggleOff
                              }
                            />
                            {userItem.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="action-buttons">
                          <button
                            className="edit-btn"
                            onClick={() => {
                              setEditingTeamMember(userItem);
                              setShowTeamMemberForm(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        <div className="empty-state">
                          <p>No users found.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (isAdminUser || isSuperAdminUser) && (
          <div className="tab-content analytics-tab">
            <AnalyticsCards
              analytics={analytics}
              userRole={userRole}
              userType={userType}
            />
            <div className="analytics-grid">
              <SalesChart data={analytics.salesData || []} />
              <TrafficSources
                sources={analytics.trafficSources || []}
                userRole={userRole}
              />
            </div>
            <TopProducts products={analytics.topProducts || []} />
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="dashboard-section">
            <h2>
              <FontAwesomeIcon icon={faUserCircle} /> Profile Settings
            </h2>
            {profileMessage.text && (
              <div className={`alert-message ${profileMessage.type}`}>
                {profileMessage.text}
              </div>
            )}
            <form onSubmit={handleProfileUpdate} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserCircle} /> Full Name
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) =>
                      setProfileData({ ...profileData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faEnvelope} /> Email
                  </label>
                  <input type="email" value={profileData.email} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faPhone} /> Phone Number
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) =>
                    setProfileData({ ...profileData, phone: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Street Address
                </label>
                <input
                  type="text"
                  value={profileData.address?.street || ""}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      address: {
                        ...profileData.address,
                        street: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={profileData.address?.city || ""}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        address: {
                          ...profileData.address,
                          city: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    value={profileData.address?.district || ""}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        address: {
                          ...profileData.address,
                          district: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={profileData.address?.country || "Sierra Leone"}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        address: {
                          ...profileData.address,
                          country: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary" disabled={updating}>
                <FontAwesomeIcon icon={faSave} />{" "}
                {updating ? "Saving..." : "Save Changes"}
              </button>
            </form>
            <div className="password-section">
              <h3>
                <FontAwesomeIcon icon={faKey} /> Change Password
              </h3>
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faKey} /> Change Password
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>
                <FontAwesomeIcon icon={faCreditCard} /> Payment Management
              </h2>
            </div>

            {/* Payment Methods */}
            <PaymentMethods onRefresh={fetchPaymentData} />

            {/* Payout Section - Only for Farmers */}
            {isFarmer && (
              <>
                <div className="payout-divider" />
                <PayoutComponent />
              </>
            )}
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === "testimonials" && (
          <TestimonialManager isAdmin={isAdminUser || isSuperAdminUser} />
        )}
      </div>

      {/* ========== MODALS ========== */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={handleUpdateOrderStatus}
          isAdmin={isAdminUser || isSuperAdminUser}
        />
      )}
      {selectedShipment && (
        <ShipmentDetailsModal
          shipment={selectedShipment}
          onClose={() => setSelectedShipment(null)}
          onUpdate={handleUpdateShipmentStatus}
          updateData={shipmentUpdateData}
          setUpdateData={setShipmentUpdateData}
          isAdmin={isAdminUser || isSuperAdminUser}
          supplier={SUPPLIER}
        />
      )}
      {showProductForm && (
        <ProductForm
          product={editingProduct}
          onClose={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          onSave={() => {
            setShowProductForm(false);
            setEditingProduct(null);
            fetchAllData();
          }}
          supplier={SUPPLIER}
        />
      )}
      {showShipmentForm && (
        <ShipmentForm
          orders={allOrders}
          onClose={() => setShowShipmentForm(false)}
          onSave={() => {
            setShowShipmentForm(false);
            fetchAllData();
          }}
          supplier={SUPPLIER}
        />
      )}
      {showTeamMemberForm && (
        <TeamMemberForm
          member={editingTeamMember}
          onClose={() => {
            setShowTeamMemberForm(false);
            setEditingTeamMember(null);
          }}
          onSave={() => {
            setShowTeamMemberForm(false);
            setEditingTeamMember(null);
            fetchAllData();
          }}
        />
      )}

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        /* Notification */
        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease;
          max-width: 450px;
        }
        .notification.success {
          background: #dcfce7;
          color: #065f46;
          border-left: 4px solid #16a34a;
        }
        .notification.error {
          background: #fee2e2;
          color: #991b1b;
          border-left: 4px solid #dc2626;
        }
        .notification button {
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
          margin-left: auto;
          padding: 0.25rem;
        }
        .notification button:hover {
          opacity: 1;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Dashboard Container */
        .dashboard-container {
          padding: 1.5rem;
          background: #F8F8F8;
          min-height: 100vh;
        }
        @media (max-width: 768px) {
          .dashboard-container { padding: 0.75rem; }
        }

        /* Header */
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .dashboard-header .header-content h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1F1F1F;
          font-family: "Montserrat", sans-serif;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .dashboard-header .header-content .role-icon {
          color: #D9A321;
        }
        .dashboard-header .header-content p {
          color: #5A5A5A;
          margin: 0;
        }
        .role-badge {
          display: inline-block;
          padding: 0.15rem 0.6rem;
          border-radius: 12px;
          font-size: 0.65rem;
          font-weight: 600;
        }
        .role-badge.super-admin { background: #D9A321; color: #ffffff; }
        .role-badge.admin { background: #3b82f6; color: #ffffff; }
        .role-badge.farmer { background: #16a34a; color: #ffffff; }

        .header-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .whatsapp-support {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 0.8rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-size: 0.75rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        .whatsapp-support:hover {
          background: #1da851;
          transform: translateY(-2px);
        }
        .logout-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 0.8rem;
          background: transparent;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          color: #5A5A5A;
          cursor: pointer;
          font-size: 0.75rem;
          transition: all 0.3s ease;
        }
        .logout-btn:hover {
          background: #fee2e2;
          border-color: #dc2626;
          color: #dc2626;
        }

        @media (max-width: 480px) {
          .dashboard-header { flex-direction: column; }
          .header-actions { width: 100%; }
          .whatsapp-support, .logout-btn { flex: 1; justify-content: center; }
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .stat-card {
          background: #ffffff;
          padding: 1.25rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          border: 1px solid rgba(0,0,0,0.04);
          transition: all 0.3s ease;
        }
        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          border-color: #D9A321;
        }
        .stat-icon {
          width: 48px;
          height: 48px;
          min-width: 48px;
          background: rgba(0,107,46,0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #006B2E;
          font-size: 1.25rem;
        }
        .stat-info h3 {
          font-size: 0.7rem;
          color: #5A5A5A;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1F1F1F;
          font-family: "Montserrat", sans-serif;
        }
        .stat-sub {
          font-size: 0.6rem;
          color: #5A5A5A;
          margin-top: 0.1rem;
        }
        .stat-sub svg { color: #D9A321; }

        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr 1fr; gap: 0.5rem; }
          .stat-card { padding: 0.75rem; }
          .stat-icon { width: 36px; height: 36px; min-width: 36px; font-size: 1rem; }
          .stat-value { font-size: 1.1rem; }
        }

        /* Alert Section */
        .alert-section {
          background: #fef3c7;
          border-left: 4px solid #f59e0b;
          padding: 1rem 1.25rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }
        .alert-section h3 { font-size: 0.875rem; color: #92400e; display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0.5rem 0; }
        .low-stock-grid { display: flex; gap: 0.75rem; flex-wrap: wrap; }
        .low-stock-card { background: #ffffff; padding: 0.3rem 0.75rem; border-radius: 6px; display: flex; gap: 0.75rem; font-size: 0.75rem; align-items: center; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
        .low-stock-card .stock-count { color: #dc2626; font-weight: 600; }

        /* Supplier Info */
        .supplier-info-section { margin-bottom: 1.5rem; }
        .supplier-info-card {
          background: linear-gradient(135deg, #006B2E, #014421);
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1rem;
          color: #ffffff;
        }
        .supplier-info-icon { font-size: 1.5rem; color: #D9A321; }
        .supplier-info-content h4 { margin: 0; font-size: 0.938rem; font-family: "Montserrat", sans-serif; }
        .supplier-info-content p { margin: 0; font-size: 0.7rem; opacity: 0.8; }
        .supplier-info-content p svg { margin: 0 0.25rem; }
        .supplier-badge { display: inline-block; font-size: 0.6rem; background: rgba(217,163,33,0.2); padding: 0.1rem 0.5rem; border-radius: 12px; margin-top: 0.15rem; }

        /* Tabs */
        .dashboard-tabs {
          display: flex;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
          border-bottom: 2px solid #e5e5e5;
          overflow-x: auto;
          padding-bottom: 0.25rem;
        }
        .dashboard-tabs button {
          padding: 0.5rem 1rem;
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.813rem;
          transition: all 0.3s ease;
          color: #5A5A5A;
          white-space: nowrap;
          border-radius: 8px 8px 0 0;
          position: relative;
        }
        .dashboard-tabs button::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 50%;
          width: 0;
          height: 3px;
          background: #D9A321;
          border-radius: 2px;
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }
        .dashboard-tabs button.active {
          color: #006B2E;
          font-weight: 600;
        }
        .dashboard-tabs button.active::after { width: 70%; }
        .dashboard-tabs button:hover:not(.active) { color: #006B2E; background: rgba(0,107,46,0.05); }
        .dashboard-tabs button svg { margin-right: 0.4rem; }

        @media (max-width: 480px) {
          .dashboard-tabs button { font-size: 0.7rem; padding: 0.35rem 0.6rem; }
        }

        /* Table */
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .data-table {
          width: 100%;
          background: #ffffff;
          border-radius: 12px;
          border-collapse: collapse;
          font-size: 0.813rem;
          min-width: 700px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .data-table th, .data-table td { padding: 0.75rem 1rem; text-align: left; border-bottom: 1px solid #f0f0f0; }
        .data-table th { font-weight: 600; color: #5A5A5A; background: #F8F8F8; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; cursor: pointer; user-select: none; }
        .data-table th:hover { color: #006B2E; }
        .data-table tr:hover { background: rgba(0,107,46,0.03); }
        .data-table .status-badge {
          display: inline-block;
          padding: 0.2rem 0.6rem;
          border-radius: 20px;
          font-size: 0.65rem;
          font-weight: 600;
          color: #ffffff;
        }
        .data-table .bulk-order-row { background: rgba(16,185,129,0.05); border-left: 3px solid #10b981; }
        .data-table .bulk-badge { display: inline-block; margin-left: 0.5rem; font-size: 0.55rem; background: #D9A321; color: #ffffff; padding: 0.1rem 0.4rem; border-radius: 10px; font-weight: 700; }

        /* Buttons */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: #D9A321;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 0.813rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .btn-primary:hover { background: #B8860B; transform: translateY(-2px); }
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: #006B2E;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 0.813rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .btn-secondary:hover { background: #014421; transform: translateY(-2px); }
        .edit-btn, .delete-btn, .view-btn { padding: 0.3rem 0.6rem; border: none; border-radius: 6px; cursor: pointer; font-size: 0.7rem; font-weight: 500; transition: all 0.3s ease; }
        .edit-btn { background: #D9A321; color: #ffffff; }
        .edit-btn:hover { background: #B8860B; }
        .delete-btn { background: #dc2626; color: #ffffff; }
        .delete-btn:hover { background: #b91c1c; }
        .view-btn { background: #3b82f6; color: #ffffff; }
        .view-btn:hover { background: #2563eb; }

        /* Section Header */
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .section-header h2 { font-size: 1.125rem; color: #006B2E; font-family: "Montserrat", sans-serif; display: flex; align-items: center; gap: 0.5rem; }
        .section-header h2 svg { color: #D9A321; }
        .filters { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .filters input, .filters select { padding: 0.4rem 0.75rem; border: 1px solid #e5e5e5; border-radius: 8px; font-size: 0.813rem; background: #ffffff; }

        /* Payment Methods */
        .payment-methods-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .payment-method-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: #ffffff;
          border: 1px solid #e5e5e5;
          border-radius: 12px;
          transition: all 0.3s ease;
        }
        .payment-method-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-color: #D9A321; }
        .payment-method-icon { width: 40px; height: 40px; background: #F8F8F8; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #006B2E; }
        .payment-method-details { flex: 1; }
        .payment-method-type { font-weight: 600; font-size: 0.875rem; }
        .payment-method-info { font-size: 0.75rem; color: #5A5A5A; }
        .default-badge { font-size: 0.55rem; background: #dcfce7; color: #16a34a; padding: 0.1rem 0.4rem; border-radius: 10px; font-weight: 600; }
        .delete-btn { background: none; border: none; color: #dc2626; cursor: pointer; padding: 0.25rem; border-radius: 50%; transition: all 0.3s ease; }
        .delete-btn:hover { background: #fee2e2; }

        /* Payout Stats */
        .payout-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .payout-stat-card {
          background: linear-gradient(135deg, #006B2E, #014421);
          color: #ffffff;
          padding: 1rem;
          border-radius: 12px;
          text-align: center;
        }
        .payout-stat-value { font-size: 1.25rem; font-weight: 700; font-family: "Montserrat", sans-serif; }
        .payout-stat-label { font-size: 0.65rem; opacity: 0.8; }

        /* User Stats */
        .user-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .user-stat-card {
          background: #ffffff;
          padding: 1rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          border: 1px solid rgba(0,0,0,0.04);
        }
        .user-stat-icon { font-size: 1.5rem; }
        .user-stat-value { font-size: 1.25rem; font-weight: 700; color: #006B2E; font-family: "Montserrat", sans-serif; }
        .user-stat-label { font-size: 0.6rem; color: #5A5A5A; text-transform: uppercase; letter-spacing: 0.3px; }

        .status-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.2rem 0.5rem;
          border-radius: 16px;
          border: none;
          cursor: pointer;
          font-size: 0.6rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        .status-toggle.active { background: #dcfce7; color: #16a34a; }
        .status-toggle.inactive { background: #fee2e2; color: #dc2626; }
        .status-toggle:disabled { opacity: 0.5; cursor: not-allowed; }

        .current-user-badge { display: inline-block; margin-left: 0.5rem; font-size: 0.55rem; background: #D9A321; color: #ffffff; padding: 0.1rem 0.4rem; border-radius: 10px; font-weight: 600; }

        /* Profile Form */
        .dashboard-section {
          background: #ffffff;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .dashboard-section h2 { color: #006B2E; font-family: "Montserrat", sans-serif; font-size: 1.125rem; margin-bottom: 1.25rem; display: flex; align-items: center; gap: 0.5rem; border-bottom: 2px solid #D9A321; padding-bottom: 0.75rem; }
        .dashboard-section h2 svg { color: #D9A321; }
        .profile-form .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        .form-group { margin-bottom: 1rem; }
        .form-group label { display: block; margin-bottom: 0.25rem; font-weight: 500; font-size: 0.813rem; color: #1F1F1F; }
        .form-group input, .form-group select { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #e5e5e5; border-radius: 8px; font-size: 0.875rem; transition: all 0.3s ease; }
        .form-group input:focus, .form-group select:focus { outline: none; border-color: #D9A321; box-shadow: 0 0 0 3px rgba(217,163,33,0.15); }
        .alert-message { padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem; font-size: 0.875rem; }
        .alert-message.success { background: #dcfce7; color: #16a34a; border-left: 4px solid #16a34a; }
        .alert-message.error { background: #fee2e2; color: #dc2626; border-left: 4px solid #dc2626; }

        .password-section { margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid #e5e5e5; }
        .password-section h3 { font-size: 1rem; color: #1F1F1F; font-family: "Montserrat", sans-serif; display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; }

        @media (max-width: 480px) {
          .profile-form .form-row { grid-template-columns: 1fr; }
        }

        .no-data { text-align: center; padding: 2rem; color: #5A5A5A; }

        /* Origin Tags */
        .origin-tag { display: inline-flex; align-items: center; gap: 0.2rem; font-size: 0.6rem; color: #006B2E; background: #e8f5e9; padding: 0.1rem 0.4rem; border-radius: 8px; margin-left: 0.25rem; }
        .origin-badge { display: inline-block; margin-left: 0.25rem; font-size: 0.5rem; background: #dcfce7; color: #16a34a; padding: 0.1rem 0.3rem; border-radius: 8px; }
        .country-tag { font-size: 0.55rem; color: #5A5A5A; margin-left: 0.25rem; }
        .size-tags { display: flex; flex-wrap: wrap; gap: 0.2rem; }
        .size-tag { font-size: 0.55rem; background: #f0f0f0; color: #1F1F1F; padding: 0.05rem 0.4rem; border-radius: 8px; }
        .preorder-tag { font-size: 0.5rem; margin-left: 0.1rem; }

        .order-type-badge, .shipment-type-badge { display: inline-block; padding: 0.15rem 0.4rem; border-radius: 10px; font-size: 0.55rem; font-weight: 600; }
        .order-type-badge.bulk_farmer, .shipment-type-badge.bulk_farmer { background: #dcfce7; color: #16a34a; }
        .order-type-badge.retail, .shipment-type-badge.retail { background: #dbeafe; color: #2563eb; }
        .shipment-type-badge.inventory { background: #fef3c7; color: #d97706; }

        .size-indicator { font-size: 0.7rem; color: #5A5A5A; }
        .size-indicator .preorder-sizes { color: #8b5cf6; }

        .shipment-stats { font-size: 0.75rem; color: #5A5A5A; }
        .shipment-stats .stat-sep { margin: 0 0.5rem; }

        .status-select { padding: 0.2rem 0.4rem; border-radius: 6px; border: 2px solid; font-size: 0.65rem; font-weight: 600; background: #ffffff; cursor: pointer; }

        .low-stock { color: #dc2626; font-weight: 600; }

        .action-buttons { display: flex; gap: 0.25rem; }
      `}</style>
    </div>
  );
};

export default Dashboard;
